import type {
    SiteTemplateDesign,
} from "./types";

export type ModernWebSystemTemplateCode =
    | "atelier-muse"
    | "boutique-service"
    | "craftsman-modern"
    | "dental-studio"
    | "groundwork-build"
    | "kinetic-club"
    | "launchpad-bold"
    | "meridian-advisory"
    | "nocturne-club"
    | "northline-stay"
    | "restaurant-brunch"
    | "restaurant-cafe-brunch"
    | "restaurant-modern"
    | "restaurant-night-bar"
    | "restaurant-pizzeria"
    | "studio-clean"
    | "takeres"
    | "torque-service"
    | "volt-flow";

export const webSystemTemplateDesignDefaults: Record<
    ModernWebSystemTemplateCode,
    SiteTemplateDesign
> = {
    "atelier-muse": {
        colors: {
            primary: "#4b1f45",
            accent: "#ed7265",
            background: "#f4ecdf",
            surface: "#fffaf0",
            text: "#1b171c",
            muted: "#655a61",
            border: "#d7ccbf",
        },
        typography: {
            heading: "Cormorant Garamond, Iowan Old Style, Georgia, serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1280px",
            sectionSpacing: "spacious",
            radius: "2px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    "boutique-service": {
        colors: {
            primary: "#9f1239",
            accent: "#c58a4a",
            background: "#fbf7f2",
            surface: "#fffdf9",
            text: "#2b1b1f",
            muted: "#756268",
            border: "#eadbd5",
        },
        typography: {
            heading: "Cormorant Garamond, Georgia, Cambria, serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1160px",
            sectionSpacing: "spacious",
            radius: "20px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "pill",
            forms: "card",
        },
    },
    "craftsman-modern": {
        colors: {
            primary: "#2f493b",
            accent: "#c96f32",
            background: "#f3eee3",
            surface: "#fffdf8",
            text: "#1e211d",
            muted: "#676a61",
            border: "#d8d0c1",
        },
        typography: {
            heading: "Georgia, Cambria, serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1240px",
            sectionSpacing: "spacious",
            radius: "8px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    "dental-studio": {
        colors: {
            primary: "#0B2638",
            accent: "#8BE8CE",
            background: "#E9F1F4",
            surface: "#FBFDFD",
            text: "#071824",
            muted: "#506571",
            border: "#A9BCC6",
        },
        typography: {
            heading: "Barlow Condensed, Arial Narrow, Roboto Condensed, sans-serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1380px",
            sectionSpacing: "spacious",
            radius: "0px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    "groundwork-build": {
        colors: {
            primary: "#252A2A",
            accent: "#F04E36",
            background: "#E7E4DC",
            surface: "#F8F7F2",
            text: "#171A1A",
            muted: "#5F6462",
            border: "#B9BBB5",
        },
        typography: {
            heading: "Manrope, Inter, system-ui, sans-serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1320px",
            sectionSpacing: "spacious",
            radius: "3px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    "kinetic-club": {
        colors: {
            primary: "#c8ff3d",
            accent: "#c8ff3d",
            background: "#151715",
            surface: "#202320",
            text: "#f3f1e8",
            muted: "#a8ada4",
            border: "rgba(243,241,232,.16)",
        },
        typography: {
            heading: "Arial Black, Space Grotesk, Inter, system-ui, sans-serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1320px",
            sectionSpacing: "spacious",
            radius: "2px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    "launchpad-bold": {
        colors: {
            primary: "#7c3aed",
            accent: "#22d3ee",
            background: "#070b17",
            surface: "#101827",
            text: "#f8fafc",
            muted: "#a9b4c7",
            border: "rgba(255,255,255,.13)",
        },
        typography: {
            heading: "Space Grotesk, Inter, system-ui, sans-serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1240px",
            sectionSpacing: "spacious",
            radius: "16px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "pill",
            forms: "line",
        },
    },
    "meridian-advisory": {
        colors: {
            primary: "#17212A",
            accent: "#B98A3D",
            background: "#F1EEE6",
            surface: "#FFFCF5",
            text: "#111820",
            muted: "#5E625F",
            border: "#C9C1B4",
        },
        typography: {
            heading: "Barlow Condensed, Arial Narrow, Newsreader, Georgia, serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1240px",
            sectionSpacing: "spacious",
            radius: "0px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    "nocturne-club": {
        colors: {
            primary: "#19d7e5",
            accent: "#ff2f7d",
            background: "#05060a",
            surface: "#10131c",
            text: "#f5f7fa",
            muted: "#969cab",
            border: "rgba(245,247,250,.14)",
        },
        typography: {
            heading: "Barlow Condensed, Arial Narrow, Oswald, Impact, sans-serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1320px",
            sectionSpacing: "spacious",
            radius: "4px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    "northline-stay": {
        colors: {
            primary: "#173831",
            accent: "#e36f45",
            background: "#f3f0e8",
            surface: "#fffaf1",
            text: "#172522",
            muted: "#5b6864",
            border: "rgba(23,56,49,.18)",
        },
        typography: {
            heading: "Iowan Old Style, Baskerville, Georgia, serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1280px",
            sectionSpacing: "spacious",
            radius: "6px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    "restaurant-brunch": {
        colors: {
            primary: "#34744d",
            accent: "#f5c84c",
            background: "#fff7e6",
            surface: "#fffcf5",
            text: "#213b2d",
            muted: "#647267",
            border: "#d9c8a9",
        },
        typography: {
            heading: "ui-rounded, Arial Rounded MT Bold, Trebuchet MS, system-ui, sans-serif",
            body: "Avenir Next, Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1280px",
            sectionSpacing: "spacious",
            radius: "28px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "pill",
            forms: "card",
        },
    },
    "restaurant-cafe-brunch": {
        colors: {
            primary: "#29463a",
            accent: "#97462d",
            background: "#f1e9da",
            surface: "#fbf7ee",
            text: "#241a15",
            muted: "#706157",
            border: "#c8b9a4",
        },
        typography: {
            heading: "Iowan Old Style, Baskerville, Times New Roman, serif",
            body: "Avenir Next, Inter, Helvetica Neue, Arial, sans-serif",
        },
        layout: {
            container: "1280px",
            sectionSpacing: "comfortable",
            radius: "2px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "outline",
            forms: "line",
        },
    },
    "restaurant-modern": {
        colors: {
            primary: "#f7941d",
            accent: "#f7941d",
            background: "#ffffff",
            surface: "#ffffff",
            text: "#3b3b3b",
            muted: "#766f68",
            border: "rgba(59,59,59,0.22)",
        },
        typography: {
            heading: "Roboto Slab, Rockwell, Georgia, serif",
            body: "EB Garamond, Georgia, serif",
        },
        layout: {
            container: "1000px",
            sectionSpacing: "comfortable",
            radius: "0px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "outline",
            forms: "line",
        },
    },
    "restaurant-night-bar": {
        colors: {
            primary: "#19d7e5",
            accent: "#ff2f7d",
            background: "#05060a",
            surface: "#10131c",
            text: "#f5f7fa",
            muted: "#969cab",
            border: "rgba(245,247,250,.14)",
        },
        typography: {
            heading: "Barlow Condensed, Arial Narrow, Oswald, Impact, sans-serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1320px",
            sectionSpacing: "spacious",
            radius: "4px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    "restaurant-pizzeria": {
        colors: {
            primary: "#ca1f26",
            accent: "#ca1f26",
            background: "#ffffff",
            surface: "#ffffff",
            text: "#090909",
            muted: "#090909",
            border: "#090909",
        },
        typography: {
            heading: "Barlow Condensed, Arial Narrow, Impact, sans-serif",
            body: "Manrope, Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1480px",
            sectionSpacing: "spacious",
            radius: "0px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "flat",
        },
    },
    "studio-clean": {
        colors: {
            primary: "#111315",
            accent: "#FF5A36",
            background: "#F2F0EA",
            surface: "#FFFFFF",
            text: "#111315",
            muted: "#656966",
            border: "#CFCEC8",
        },
        typography: {
            heading: "Barlow Condensed, Arial Narrow, Manrope, sans-serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1340px",
            sectionSpacing: "spacious",
            radius: "0px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    takeres: {
        colors: {
            primary: "#0f766e",
            accent: "#84cc16",
            background: "#f3f7f5",
            surface: "#ffffff",
            text: "#10211f",
            muted: "#60726f",
            border: "#d8e4e0",
        },
        typography: {
            heading: "Sora, Inter, system-ui, sans-serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1240px",
            sectionSpacing: "spacious",
            radius: "18px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "pill",
            forms: "card",
        },
    },
    "torque-service": {
        colors: {
            primary: "#111315",
            accent: "#F4511E",
            background: "#F2F0EA",
            surface: "#FFFFFF",
            text: "#111315",
            muted: "#666A68",
            border: "#D2D1CC",
        },
        typography: {
            heading: "Barlow Condensed, Arial Narrow, sans-serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1380px",
            sectionSpacing: "spacious",
            radius: "0px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "line",
        },
    },
    "volt-flow": {
        colors: {
            primary: "#081a2f",
            accent: "#c9ff45",
            background: "#f3f6f7",
            surface: "#ffffff",
            text: "#07111f",
            muted: "#617181",
            border: "#cbd6dd",
        },
        typography: {
            heading: "Space Grotesk, Sora, Inter, system-ui, sans-serif",
            body: "Inter, system-ui, sans-serif",
        },
        layout: {
            container: "1280px",
            sectionSpacing: "spacious",
            radius: "0px",
        },
        components: {
            header: "builder",
            footer: "builder",
            buttons: "solid",
            forms: "card",
        },
    },
};
